// Shared text preparation only: no reads, requests, sends or implicit defaults.
export const normalizePlaceholderKey = key => String(key).toLowerCase().replace(/[\s._]/g, '');
const text = value => typeof value === 'string' || typeof value === 'number' ? String(value).trim() : '';
const personName = person => typeof person === 'string' ? person : text(person?.display_name || person?.fullName || [person?.name, person?.surname].filter(Boolean).join(' ') || person?.username);

export function placeholderCustomerName(context = {}) {
  const chat = context.chat || {}, customer = context.customer || {}, query = context.query || {};
  const identities = chat._canonical?.participant?.identities || chat.participant?.identities || {};
  const identifiers = [context.customerPhone, customer.customer_mobile, customer.phone, customer.username, query.customer_phone, chat.phone, chat.customer_mobile, chat.username, identities.phone, identities.username].map(text).filter(Boolean);
  const usable = value => {
    const name = text(value);
    return name && !/^(unknown|unknown customer|—)$/i.test(name)
      && !/^\+?[\d\s().-]{7,}$/.test(name)
      && !identifiers.some(identifier => identifier.replace(/^@/, '').toLowerCase() === name.replace(/^@/, '').toLowerCase());
  };
  return [customer.customer_real_name, query.customer?.customer_real_name, query.customer_real_name, chat.customer_real_name,
    customer.profile_name, customer.customer_name, customer.name, query.customer?.customer_name,
    chat.profile_name, chat._canonical?.business_context?.customer_name, context.customerName, query.customer_name,
    chat.customer_name, chat.participant?.display_name, chat._canonical?.participant?.display_name].find(usable) || '';
}

export function placeholderValue(key, context = {}) {
  const authState = context.authState || {}, chat = context.chat || {}, query = context.query || {};
  const actor = personName(authState.user) || text(authState.name || authState.username);
  const assignment = chat._canonical?.assignment || chat.assignment;
  const assignee = context.assignedAgent !== undefined ? context.assignedAgent
    : assignment ? assignment.assigned_to
      : chat.agent || query.assigned_user || query.assigned_to_name || query.assigned_agent || context.userMap?.[query.assigned_to]
        || (typeof query.assigned_to === 'object' ? query.assigned_to : query.assigned_to ? { id: query.assigned_to } : null);
  const assignedName = assignee ? personName(assignee) : actor;
  // Keep the old intentional distinction before normalizing dots/underscores.
  if (key.trim().toLowerCase() === 'operator.first_name') return actor.split(/\s+/)[0];
  switch (normalizePlaceholderKey(key)) {
    case 'name': case 'customername': return placeholderCustomerName(context);
    case 'operatorfirstname': return assignedName.split(/\s+/)[0];
    case 'operatorname': return assignedName;
    case 'queryid': case 'querynumber': return text(context.queryId ?? query.query_id ?? query.query_number);
    default: return '';
  }
}

export function fillMessagePlaceholders(template, context = {}) {
  const valueKey = key => key.trim().toLowerCase() === 'operator.first_name' ? 'operator.first_name' : normalizePlaceholderKey(key);
  const values = new Map(Object.entries(context.values || {}).map(([key, value]) => [valueKey(key), value]));
  const fill = source => String(source ?? '').replace(/\{\{\s*([^{}]+?)\s*\}\}/g, (original, key) => {
    const value = values.has(valueKey(key)) ? values.get(valueKey(key)) : placeholderValue(key, context);
    return value !== null && value !== undefined && String(value).trim() !== '' ? String(value) : original;
  });
  const expanded = String(template ?? '').replace(/\{\{\s*#parts\s*\}\}([\s\S]*?)\{\{\s*\/parts\s*\}\}/gi, (original, block) => {
    if (!Array.isArray(context.parts)) return original;
    return context.parts.map(part => context.renderPart ? context.renderPart(block, part)
      : fillMessagePlaceholders(block, { ...context, parts: undefined, values: { ...context.values, part_name: part.part_name } })).join('');
  });
  return fill(expanded);
}

export function unfilledPlaceholders(text) {
  return [...new Set([...String(text ?? '').matchAll(/\{\{\s*([^{}]+?)\s*\}\}/g)].map(match => match[1].trim()))];
}
